import { fn } from '@ember/helper';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismButton, PrismCard } from '@mylighthouse/prism-ember';
import { t, tKey } from 'ember-intl';
import { and, eq, not } from 'ember-truth-helpers';
import type { EligibilityQuestionAnswer } from 'lumen/models/otbselfservice';
import type { EligibilityQuestion } from 'lumen/models/pmsdatasource';
import {
  ROUTE_NAME_FOR_STAGE_EDITORS,
  type Stage,
  STAGES,
} from 'lumen/utils/constants/otb-selfservice-constants';

interface BasketItem {
  id: string;
  stages: [Stage, ...Stage[]];
  titleKey: string | undefined;
}

const BASKET_ITEMS = {
  GET_STARTED: {
    id: 'get_started',
    titleKey: tKey('settings.my_otb.summary_basket.get_started'),
    stages: [STAGES.INTRO_PAGE],
  } as BasketItem,
  PMS: {
    id: 'pms',
    titleKey: 'general.pms',
    stages: [STAGES.PMS_SELECTION_PAGE],
  } as BasketItem,
  PMS_VERSION: {
    id: 'pms_version',
    titleKey: tKey('settings.my_otb.pms_version'),
    stages: [STAGES.PMS_SELECTION_PAGE],
  } as BasketItem,
  PMS_ELIGIBILITY_QUESTION: {
    id: 'pms_eligibility_question',
    titleKey: tKey('settings.my_otb.summary_basket.pms_information'),
    stages: [STAGES.PMS_ELIGIBILITY_QUESTIONS_PAGE],
  } as BasketItem,
  OTHER_SYSTEM: {
    id: 'other_system',
    titleKey: tKey('settings.my_otb.summary_basket.other_system'),
    stages: [
      STAGES.OTHER_SYSTEM_SELECTION_PAGE,
      STAGES.OTHER_SYSTEM_SELECTION_PREFILLED_PAGE,
    ],
  } as BasketItem,
  OTHER_SYSTEM_ELIGIBILITY_QUESTION: {
    id: 'other_system_eligibility_question',
    titleKey: undefined,
    stages: [STAGES.OTHER_SYSTEM_ELIGIBILITY_QUESTIONS_PAGE],
  } as BasketItem,
};

interface SettingsMyotbSummaryBasketSignature {
  Args: {};
}

export default class SettingsMyotbSummaryBasket extends Component<SettingsMyotbSummaryBasketSignature> {
  @service declare intl: Services['intl'];
  @service declare myotbSs: Services['myotb-ss'];
  @service declare router: Services['router'];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get canEditSetup() {
    // Pages where the setup can't be edited.
    const noEditPages = [
      STAGES.INTEGRATION_ACTIVE_PAGE,
      STAGES.SETUP_IN_PROGRESS_PAGE,
    ];

    return !noEditPages.some((noEditPage) =>
      // @ts-expect-error: Object is possibly 'null'.
      this.router.currentRouteName.includes(
        ROUTE_NAME_FOR_STAGE_EDITORS[noEditPage],
      ),
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get currentStageItems() {
    const { pmsOrOtherSystem } = this.myotbSs;

    const stage = this.myotbSs.otbSelfService?.stage;

    const currentStageItems = [];

    if (
      stage === STAGES.INTRO_PAGE &&
      // @ts-expect-error: Object is possibly 'null'.
      this.router.currentRouteName.includes(
        ROUTE_NAME_FOR_STAGE_EDITORS[STAGES.PMS_SELECTION_PAGE],
      )
    ) {
      // In a fresh flow, the stage on the PMS selection page is still STAGES.INTRO_PAGE, but we still want to show the PMS basket item.
      currentStageItems.push(BASKET_ITEMS.PMS);
    } else if (
      pmsOrOtherSystem?.eligibilityQuestions?.length &&
      (stage === STAGES.PMS_ELIGIBILITY_QUESTIONS_PAGE ||
        stage === STAGES.OTHER_SYSTEM_ELIGIBILITY_QUESTIONS_PAGE)
    ) {
      // On the eligibility question pages, we add each question as a basket item.
      const basketItem =
        stage === STAGES.OTHER_SYSTEM_ELIGIBILITY_QUESTIONS_PAGE
          ? BASKET_ITEMS.OTHER_SYSTEM_ELIGIBILITY_QUESTION
          : BASKET_ITEMS.PMS_ELIGIBILITY_QUESTION;

      // We don't need to pass the eligibility questions answers to the mapEligibilityQuestionsToBasketItems function here because
      // we won't add the answers as basket item values anyway.
      const eligibilityQuestionBasketItems =
        this.mapEligibilityQuestionsToBasketItems(
          pmsOrOtherSystem.eligibilityQuestions,
          null,
          basketItem,
        );
      eligibilityQuestionBasketItems.forEach(
        (eligibilityQuestionBasketItem) => {
          currentStageItems.push(eligibilityQuestionBasketItem);
        },
      );
    } else {
      Object.values(BASKET_ITEMS)
        .filter((basketItem) => basketItem.stages.includes(stage as Stage))
        .forEach((basketItem) => currentStageItems.push(basketItem));
    }

    return currentStageItems;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get items() {
    const { otbSelfService } = this.myotbSs;

    const pmsVersion = otbSelfService?.pmsVersion;
    const otherPmsName = otbSelfService?.otherPmsName;
    const pmsDatasource = otbSelfService?.pmsDatasource;
    const eligibilityQuestionsAnswers =
      otbSelfService?.eligibilityQuestionsAnswers;
    const otherSystem = otbSelfService?.otherSystem;
    const customOtherSystemName = otbSelfService?.customOtherSystemName;
    const otherSystemEligibilityQuestionsAnswers =
      otbSelfService?.otherSystemEligibilityQuestionsAnswers;

    const items = [];

    if (pmsVersion) {
      // If the PMS has no versions, the pmsName and displayName will be the same. In that case, don't show a PMS version in the summary basket.
      const showPmsVersion = pmsVersion.pmsName !== pmsVersion.displayName;

      items.push({
        ...BASKET_ITEMS.PMS,
        value: otbSelfService.pmsVersion.pmsName,
      });

      if (showPmsVersion) {
        items.push({
          ...BASKET_ITEMS.PMS_VERSION,
          value: otbSelfService.pmsVersion.displayName,
        });
      }
    } else if (otherPmsName) {
      items.push({
        ...BASKET_ITEMS.PMS,
        value: otherPmsName,
      });
    }

    if (pmsDatasource) {
      const { eligibilityQuestions } = pmsDatasource;

      if (eligibilityQuestions && eligibilityQuestionsAnswers) {
        const eligibilityQuestionBasketItems =
          this.mapEligibilityQuestionsToBasketItems(
            eligibilityQuestions,
            eligibilityQuestionsAnswers,
            BASKET_ITEMS.PMS_ELIGIBILITY_QUESTION,
          );
        eligibilityQuestionBasketItems.forEach(
          (eligibilityQuestionBasketItem) =>
            items.push(eligibilityQuestionBasketItem),
        );
      }
    }

    if (otherSystem) {
      const { eligibilityQuestions } = otherSystem;

      items.push({
        ...BASKET_ITEMS.OTHER_SYSTEM,
        value: otherSystem.otherSystemDisplayName,
      });

      if (eligibilityQuestions && otherSystemEligibilityQuestionsAnswers) {
        const eligibilityQuestionBasketItems =
          this.mapEligibilityQuestionsToBasketItems(
            eligibilityQuestions,
            otherSystemEligibilityQuestionsAnswers,
            BASKET_ITEMS.OTHER_SYSTEM_ELIGIBILITY_QUESTION,
          );
        eligibilityQuestionBasketItems.forEach(
          (eligibilityQuestionBasketItem) =>
            items.push(eligibilityQuestionBasketItem),
        );
      }
    } else if (customOtherSystemName) {
      items.push({
        ...BASKET_ITEMS.OTHER_SYSTEM,
        value: customOtherSystemName,
      });
    }

    return items;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get showAdjustYourSetupButton() {
    // Pages where we show the 'Adjust your setup' button instead of small 'Edit' buttons.
    const adjustYourSetupPages = [
      STAGES.VALIDATING_DATA_PAGE,
      STAGES.PMS_UNSUPPORTED_PAGE,
    ];

    return adjustYourSetupPages.some((adjustYourSetupPage) =>
      // @ts-expect-error: Object is possibly 'null'.
      this.router.currentRouteName.includes(
        ROUTE_NAME_FOR_STAGE_EDITORS[adjustYourSetupPage],
      ),
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  mapEligibilityQuestionsToBasketItems(
    eligibilityQuestions: EligibilityQuestion[],
    eligibilityQuestionsAnswers: EligibilityQuestionAnswer[] | null,
    basketItem: BasketItem,
  ) {
    return eligibilityQuestions.map((eligibilityQuestion) => {
      let value = null;

      const eligibilityQuestionAnswers = eligibilityQuestionsAnswers?.find(
        (eligibilityQuestionsAnswersEntry) =>
          eligibilityQuestionsAnswersEntry.name === eligibilityQuestion.name,
      );

      if (eligibilityQuestionAnswers) {
        value = eligibilityQuestionAnswers.answers
          .map((eligibilityQuestionAnswer) =>
            this.intl.t(eligibilityQuestionAnswer),
          )
          .join(', ');
      }

      return {
        ...basketItem,
        titleKey: eligibilityQuestion.short_name,
        value,
      };
    });
  }

  @action onClickAdjustYourSetup(): void {
    this.router.transitionTo(
      ROUTE_NAME_FOR_STAGE_EDITORS[STAGES.PMS_SELECTION_PAGE],
    );
  }

  @action onClickEdit(
    item: (typeof BASKET_ITEMS)[keyof typeof BASKET_ITEMS],
  ): void {
    this.router.transitionTo(ROUTE_NAME_FOR_STAGE_EDITORS[item.stages[0]]);
  }

  <template>
    <PrismCard class="myotb-summary-basket">
      <:header as |Header|>
        <Header.title>
          {{t "settings.my_otb.summary_basket.title"}}
        </Header.title>
      </:header>

      <:content>
        <div class="myotb-summary-basket__content flex flex-col">
          {{#each this.items as |item|}}
            <div class="myotb-summary-basket__content__basket-item">
              <p class="text-300--bold">
                {{#if item.titleKey}}
                  {{t item.titleKey}}
                {{/if}}
              </p>

              {{#if item.value}}
                <div class="mt-100 flex items-center justify-start gap-200">
                  <p class="text-300--regular">{{item.value}}</p>

                  {{#if
                    (and this.canEditSetup (not this.showAdjustYourSetupButton))
                  }}
                    <PrismButton
                      @buttonType="secondary"
                      @iconBefore="edit-pencil"
                      @label={{t "general.edit"}}
                      @onClick={{fn this.onClickEdit item}}
                      @size="small"
                    />
                  {{/if}}
                </div>
              {{/if}}
            </div>
          {{/each}}

          {{#each this.currentStageItems as |currentStageItem|}}
            <p
              class="myotb-summary-basket__content__basket-item text-300--regular
                {{if
                  (eq currentStageItem BASKET_ITEMS.GET_STARTED)
                  ''
                  'myotb-summary-basket__content__basket-item--current text-300--bold'
                }}"
            >
              {{#if currentStageItem.titleKey}}
                {{t currentStageItem.titleKey}}
              {{/if}}
            </p>
          {{/each}}

          {{#if (and this.canEditSetup this.showAdjustYourSetupButton)}}
            <div class="mt-400">
              <PrismButton
                @buttonType="secondary"
                @iconBefore="edit-pencil"
                @label={{t "settings.my_otb.summary_basket.adjust_setup"}}
                @onClick={{this.onClickAdjustYourSetup}}
                @size="small"
              />
            </div>
          {{/if}}
        </div>
      </:content>
    </PrismCard>
  </template>
}
